import React, { useEffect, useState, useCallback } from "react";
import LText from "@components/LText";
import LBox from "@components/LBox";
import LPressable from "@components/LPressable";
import { router } from "expo-router";
import IntroLayout from "./IntroLayout";
import PeopleStatPill from "./PeopleStatPill";
import { trackOnb } from "../analytics/trackOnboarding";
import {
  useOnboardingStore,
  type ListenerType,
} from "../store/onboardingStore";
import { fonts, theme } from "@theme";
import { Platform } from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withSpring,
  interpolateColor,
  Easing,
  type SharedValue,
  withDelay,
  FadeIn,
} from "react-native-reanimated";
import * as Haptics from "expo-haptics";
import { captureException } from "@utils/captureException";

const ACCENT = "#3F51B5";

const options: {
  label: string;
  value: ListenerType;
  badge: string;
  description: string;
  peopleStat: string;
}[] = [
  {
    label: "Casual listener",
    value: "casual",
    badge: "~1 hr/day",
    description: "Background music, Commute playlists",
    peopleStat: "+3.4M people in common",
  },
  {
    label: "Music lover",
    value: "music-lover",
    badge: "~2 hrs/day",
    description: "Always have something on, Curated taste",
    peopleStat: "+4.1M people in common",
  },
  {
    label: "Superfan",
    value: "superfan",
    badge: "3+ hrs/day",
    description: "Non stop, deep into discovery and playlists",
    peopleStat: "+2.5M people in common",
  },
];

const RADIO_SPRING = { damping: 20, stiffness: 400, mass: 0.8 };

const RadioCircle = ({
  outerScale,
  innerScale,
  borderProgress,
}: {
  outerScale: SharedValue<number>;
  innerScale: SharedValue<number>;
  borderProgress: SharedValue<number>;
}) => {
  const outerStyle = useAnimatedStyle(() => ({
    transform: [{ scale: outerScale.value }],
    borderColor: interpolateColor(
      borderProgress.value,
      [0, 1],
      [theme.colors.greyBorder, ACCENT],
    ),
  }));

  const innerStyle = useAnimatedStyle(() => ({
    transform: [{ scale: innerScale.value }],
    opacity: innerScale.value,
  }));

  return (
    <Animated.View
      style={[
        {
          width: 22,
          height: 22,
          borderRadius: 11,
          borderWidth: 2,
          alignItems: "center",
          justifyContent: "center",
        },
        outerStyle,
      ]}
    >
      <Animated.View
        style={[
          {
            width: 12,
            height: 12,
            borderRadius: 6,
            backgroundColor: ACCENT,
          },
          innerStyle,
        ]}
      />
    </Animated.View>
  );
};

const OptionCard = ({
  option,
  isSelected,
  onPress,
}: {
  option: (typeof options)[number];
  isSelected: boolean;
  onPress: () => void;
}) => {
  const bgProgress = useSharedValue(isSelected ? 1 : 0);
  const outerScale = useSharedValue(1);
  const innerScale = useSharedValue(isSelected ? 1 : 0);
  const borderProgress = useSharedValue(isSelected ? 1 : 0);

  useEffect(() => {
    bgProgress.value = withTiming(isSelected ? 1 : 0, {
      duration: 250,
      easing: Easing.out(Easing.cubic),
    });
    borderProgress.value = withSpring(isSelected ? 1 : 0, RADIO_SPRING);
    if (isSelected) {
      outerScale.value = 1.5;
      // outer ring squeezes down slightly then settles
      outerScale.value = withSpring(0.95, RADIO_SPRING);
      // inner dot scales up from 0
      innerScale.value = 0;
      innerScale.value = withDelay(100, withSpring(1, RADIO_SPRING));
    } else {
      outerScale.value = withSpring(1, RADIO_SPRING);
      innerScale.value = withSpring(0, RADIO_SPRING);
    }
  }, [isSelected, bgProgress, outerScale, innerScale, borderProgress]);

  const cardAnimatedStyle = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(
      bgProgress.value,
      [0, 1],
      [theme.colors.closeButtonBg, "#FFFFFF"],
    ),
    borderColor: interpolateColor(
      bgProgress.value,
      [0, 0.5, 1],
      [theme.colors.greyBorder, theme.colors.greyBorder, "transparent"],
    ),
  }));

  return (
    <LPressable
      sentryEventName={`quiz1_select_${option.value}`}
      onPress={onPress}
    >
      <Animated.View
        style={[
          {
            flexDirection: "row",
            alignItems: "flex-start",
            gap: 12,
            paddingVertical: 18,
            paddingHorizontal: 18,
            borderRadius: theme.borderRadii.l,
            borderWidth: 1,
          },
          cardAnimatedStyle,
        ]}
      >
        <LBox style={{ marginTop: 2 }}>
          <RadioCircle
            outerScale={outerScale}
            innerScale={innerScale}
            borderProgress={borderProgress}
          />
        </LBox>
        <LBox flex={1}>
          <LBox flexDirection="row" alignItems="center" gap="s2" mb="s1">
            <LText
              fontSize={18}
              fontFamily={fonts.semiBold}
              color={isSelected ? "screenBg" : "white"}
            >
              {option.label}
            </LText>
            <LBox
              style={{ backgroundColor: ACCENT }}
              borderRadius="max"
              paddingHorizontal="s3"
              paddingVertical="s1"
            >
              <LText fontSize={12} fontFamily={fonts.semiBold} color="white">
                {option.badge}
              </LText>
            </LBox>
          </LBox>
          <LText
            fontSize={14}
            fontFamily={fonts.regular}
            color={isSelected ? "additiveText" : "whiteTranslucent60"}
          >
            {option.description}
          </LText>
        </LBox>
      </Animated.View>
    </LPressable>
  );
};

const Quiz1Screen = () => {
  const [selected, setSelected] = useState<ListenerType>("music-lover");
  const setQuizListenerType = useOnboardingStore((s) => s.setQuizListenerType);

  useEffect(() => {
    trackOnb("signup", "quiz-1", "view");
  }, []);

  const peopleStat =
    options.find((o) => o.value === selected)?.peopleStat ??
    options[0].peopleStat;

  const handleSelect = useCallback((value: ListenerType) => {
    setSelected(value);
    trackOnb("signup", "quiz-1", "answer-selected", { answer: value });
    if (Platform.OS !== "web") {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch((error) => {
        captureException(error, {
          channel: "ignore",
          area: "haptics",
          operation: "quiz-1-select-impact",
        });
      });
    }
  }, []);

  const handleContinue = () => {
    if (!selected) return;
    setQuizListenerType(selected);
    router.navigate("/onboarding/quiz-2");
  };

  return (
    <IntroLayout
      totalSteps={2}
      currentStep={1}
      buttonLabel="Continue"
      buttonEventName="quiz1_continue"
      onButtonPress={handleContinue}
      buttonDisabled={!selected}
    >
      <Animated.View entering={FadeIn.duration(400).delay(300)}>
        <LText
          fontSize={32}
          fontFamily={fonts.bold}
          lineHeight={38}
          textAlign="center"
          mb="s3"
        >
          How much do you listen to music?
        </LText>

        <LBox alignItems="center" mb="l2" style={{ minHeight: 36 }}>
          <PeopleStatPill text={peopleStat} />
        </LBox>

        <LBox gap="s3">
          {options.map((option) => (
            <OptionCard
              key={option.value}
              option={option}
              isSelected={selected === option.value}
              onPress={() => handleSelect(option.value)}
            />
          ))}
        </LBox>
      </Animated.View>
    </IntroLayout>
  );
};

export default Quiz1Screen;
